import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import AppTable, { ColumnConfig } from "../../../../components/AppTable/AppTable";
import TextPill from "../../../../components/TextPill";
import { RootState } from "../../../../redux/store";
import { setField } from "../../../../redux/improvementPlanSlice";
import { SectionPermissionTypeEnum } from "../../../../types/SectionPermissionTypesEnum";
import { formatDate } from "../../../../utils/formatters/DateFormatter";
import ImprovementPlanDemandsModal from "../modals/ImprovementPlanDemandsModal";
import { ImprovementPlanDemandListItemDto, ImprovementPlanFormDto } from "../../types";
import { SelectItemDto } from "../../../../types/SelectItemDto";
import { getStatusBudgeColorStyle } from "../../../claims/utils/claimStatusStyle";
import { ClaimStatusEnum } from "../../../claims/types/ClaimStatusEnum";

type ImprovementPlanDemandsTabProps = {
    assignedToOptions: SelectItemDto[];
};

export default function ImprovementPlanDemandsTab({
    assignedToOptions
}: ImprovementPlanDemandsTabProps) {
    const { t } = useTranslation(["improvementPlan", "claim", "common"]);
    const dispatch = useDispatch();
    const demands = useSelector((state: RootState) => state.improvementPlanForm.data.demands) ?? [];

    const [showModal, setShowModal] = useState(false);

    const columns: ColumnConfig<ImprovementPlanDemandListItemDto>[] = [
        { header: t("common:form.fields.id"), key: "id", render: (item) => <b>{item.id}</b> },
        { header: t("improvementPlan:demandsTable.patientName"), key: "patientName" },
        { header: t("improvementPlan:demandsTable.registerDate"), key: "registerDate", render: (item) => <>{formatDate(item.registerDate)}</> },
        { header: t("improvementPlan:demandsTable.claimType"), key: "requestType" },
        {
            header: t("common:form.fields.status"),
            key: "status",
            render: (item) => {
                const style = getStatusBudgeColorStyle(ClaimStatusEnum[item.status as keyof typeof ClaimStatusEnum]);
                return <TextPill
                    label={t(`claim:status.${item.status}`)}
                    backgroundColor={style?.backgroundColor ?? "lightgrey"}
                    textColor={style?.color ?? "#fff"}
                    fontWeight={style?.fontWeight ?? 600}
                />
            }
        },
        {
            header: t("improvementPlan:demandsTable.hasAlert"),
            key: "hasAlert",
            render: (item) => (
                <TextPill
                    label={item.hasAlert ? t("common:yes") : t("common:no")}
                    backgroundColor={item.hasAlert ? "#ff7576" : "white"}
                    textColor={item.hasAlert ? "white" : "black"}
                    minWidth="70px"
                />
            )
        },
        { header: t("improvementPlan:demandsTable.comments"), key: "comments" }
    ];

    const handleOpenModal = () => {
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleAddDemands = (selectedDemands: ImprovementPlanDemandListItemDto[]) => {
        if (!selectedDemands.length) return;
        const existingIds = new Set((demands ?? []).map((demand) => demand.id));
        const mergedDemands = [...(demands ?? [])];

        selectedDemands.forEach((demand) => {
            if (!existingIds.has(demand.id)) {
                mergedDemands.push(demand);
            }
        });

        dispatch(setField({ field: "demands", value: mergedDemands as ImprovementPlanFormDto["demands"] }));
    };

    const handleDeleteDemand = (demandToDelete: ImprovementPlanDemandListItemDto) => {
        const updatedDemands = (demands ?? []).filter((demand) => demand.id !== demandToDelete.id);
        dispatch(setField({ field: "demands", value: updatedDemands as ImprovementPlanFormDto["demands"] }));
    };

    return (
        <>
            <Row className="mb-2 d-flex align-items-center justify-content-between">
                <Col xs="auto">
                    <h5 className="mb-0">{t("improvementPlan:tabs.demands")}</h5>
                </Col>
                <Col xs="auto">
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={handleOpenModal}
                    >
                        {t("improvementPlan:demandsButtonNew")}
                    </Button>
                </Col>
            </Row>

            <AppTable<ImprovementPlanDemandListItemDto>
                data={demands ?? []}
                columns={columns}
                emptyMessage={t("improvementPlan:demandsEmptyMessage")}
                t={t}
                hasActions={true}
                onDelete={handleDeleteDemand}
                hasPagination={false}
                sectionPermission={SectionPermissionTypeEnum.IMPROVEMENT_PLANS}
            />
            <ImprovementPlanDemandsModal
                show={showModal}
                assignedToOptions={assignedToOptions}
                excludedDemandIds={(demands ?? []).map((demand) => demand.id)}
                onClose={handleCloseModal}
                onAddDemands={handleAddDemands}
            />
        </>
    );
}
